import { Button, Dropdown, Menu, Space } from '@arco-design/web-react';
import { IconDown } from '@arco-design/web-react/icon';
import type { Ticket } from '../../api/ticket';

// 合法状态流转：open→processing→resolved→closed，resolved 可重开回 processing；completed 为审核通过终态
const STATUS_ACTIONS: Record<string, { next: string; label: string }[]> = {
  open: [{ next: 'processing', label: '开始处理' }],
  processing: [{ next: 'resolved', label: '已解决' }],
  resolved: [
    { next: 'closed', label: '关闭' },
    { next: 'processing', label: '重新打开' },
  ],
  closed: [],
  completed: [],
};

export interface TicketOpsBarProps {
  /** 工单详情（状态决定可用的流转动作） */
  detail: Ticket;
  /** 操作权：创建人/指派人/主管/admin（与后端 ticket.CanManage 同口径，由入口侧判定） */
  canOperate: boolean;
  /**
   * 是否存在在途流程实例（批次 A）。「提交审批」是发起审批流的唯一入口，故判据是
   * 「无在途实例」而不是「无实例指针」：指针残留但实例已终态时同样要给出入口。
   */
  hasInflightFlow: boolean;
  /**
   * 抽屉 footer 挂载目标（仅侧滑抽屉传抽屉 footer 容器）：
   * 有该容器时「更多」弹层挂到它上面（与按钮同处不滚动区域）；缺省沿用 Arco 默认容器。
   */
  opsTarget?: HTMLElement | null;
  onSubmitWorkflow: () => void;
  onFlow: (next: string, label: string) => void;
  onOpenAssign: () => void;
  onOpenSwitch: () => void;
  onUrge: () => void;
  onEscalate: () => void;
  onOpenLink: () => void;
}

/**
 * 工单详情底部操作条（提交审批 / 状态流转 / 更多）：抽屉场景由入口侧 Portal 到 drawer footer 固定不动，
 * 正文内容随抽屉滚动；本分片只做展示与分发，写操作全部由入口侧传入。
 */
export default function TicketOpsBar({
  detail,
  canOperate,
  hasInflightFlow,
  opsTarget,
  onSubmitWorkflow,
  onFlow,
  onOpenAssign,
  onOpenSwitch,
  onUrge,
  onEscalate,
  onOpenLink,
}: TicketOpsBarProps) {
  return (
    <Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
      {(detail.status === 'open' || detail.status === 'processing') && !hasInflightFlow && canOperate && (
        <Button type="primary" status="success" onClick={onSubmitWorkflow}>
          提交审批
        </Button>
      )}
      {canOperate && (STATUS_ACTIONS[detail.status || ''] || []).map((a, i) => (
        <Button key={a.next + a.label} type={i === 0 ? 'primary' : 'outline'} onClick={() => onFlow(a.next, a.label)}>
          {a.label}
        </Button>
      ))}
      <Dropdown
        trigger="click"
        position="br"
        // 操作条被 Portal 到抽屉 footer 后不能沿用 Drawer 注入的弹层容器：那是可滚动的正文区
        // （.arco-drawer-content），弹层挂进去会随正文滚动，而按钮固定在 footer 不动——两者脱开。
        // 有 opsTarget（footer 内已定位容器）时挂到它上面，弹层与按钮同处不滚动区域；
        // 原位渲染（无 opsTarget）沿用 Arco 默认容器，弹层与按钮一起随正文滚动才是对的。
        getPopupContainer={opsTarget ? () => opsTarget : undefined}
        droplist={
          <Menu
            onClickMenuItem={(key) => {
              if (key === 'assign') onOpenAssign();
              else if (key === 'switch') onOpenSwitch();
              else if (key === 'urge') onUrge();
              else if (key === 'escalate') onEscalate();
              else if (key === 'link') onOpenLink();
            }}
          >
            <Menu.Item key="assign">转派</Menu.Item>
            {detail.status !== 'closed' && detail.status !== 'completed' && <Menu.Item key="switch">切换流程</Menu.Item>}
            <Menu.Item key="urge">催办</Menu.Item>
            <Menu.Item key="escalate">提级</Menu.Item>
            <Menu.Item key="link">关联</Menu.Item>
          </Menu>
        }
      >
        <Button icon={<IconDown />}>更多</Button>
      </Dropdown>
    </Space>
  );
}
